import { ref, computed } from 'vue'
import { Availability } from "@/services/bikes/types"



export function useDateRange(availabilities: Availability) {
  const pickupDate = ref<Date | null>(null)
  const dropoffDate = ref<Date | null>(null)

  // Get all available pickup dates
  const availablePickupDates = computed(() => {
    const dates = new Set<string>()
    Object.values(availabilities.days).forEach(day => {
      if (day.pickup && day.pickup.date) {
        dates.add(day.pickup.date)
      }
    })
    return dates
  })

  // Get all available dropoff dates
  const availableDropoffDates = computed(() => {
    const dates = new Set<string>()
    Object.values(availabilities.days).forEach(day => {
      if (day.dropoff && day.dropoff.date) {
        dates.add(day.dropoff.date)
      }
    })
    return dates
  })

  const minDropoffDate = computed(() => {
    let minDate = new Date(availabilities.start_date)

    if (pickupDate.value) {
      minDate = new Date(pickupDate.value)
      minDate.setDate(minDate.getDate() + 1)
    }
    return minDate
  })

  const maxDropoffDate = computed(() => {
    let maxDate = new Date(availabilities.end_date)

    if (disabledPickupDates.value.length > 0) {
      if (pickupDate.value) {
        const sortedDisabledDates = [...disabledPickupDates.value]
          .filter(date => date > (pickupDate.value as Date))
          .sort((a, b) => a.getTime() - b.getTime())
        if (sortedDisabledDates.length > 0) {
          maxDate = sortedDisabledDates[0]
        }
      }
    }

    return maxDate
  })

  // Calculate disabled pickup dates
  const disabledPickupDates = computed(() => {
    const deploymentStartDate = new Date(availabilities.start_date)
    const deploymentEndDate = new Date(availabilities.end_date)
    const disabled = []
    
    const currentDate = new Date(deploymentStartDate)
    
    while (currentDate <= deploymentEndDate) {
      const dateString = currentDate.toISOString().split('T')[0]
      
      if (!availablePickupDates.value.has(dateString)) {
        disabled.push(new Date(currentDate))
      }
      
      currentDate.setDate(currentDate.getDate() + 1)
    }
    
    return disabled
  })

  // Calculate disabled dropoff dates
  const disabledDropoffDates = computed(() => {
    const deploymentStartDate = new Date(availabilities.start_date)
    const deploymentEndDate = new Date(availabilities.end_date)
    const disabled = []
    
    const currentDate = new Date(deploymentStartDate)
    
    while (currentDate <= deploymentEndDate) {
      const dateString = currentDate.toISOString().split('T')[0]
      
      if (!availableDropoffDates.value.has(dateString)) {
        disabled.push(new Date(currentDate))
      }
      
      currentDate.setDate(currentDate.getDate() + 1)
    }
    
    return disabled
  })

  // Check if the selected date range has any conflicts
  const hasDateRangeConflict = computed(() => {
    if (!pickupDate.value || !dropoffDate.value) return false
    
    const pickupDateString = pickupDate.value.toISOString().split('T')[0]
    const dropoffDateString = dropoffDate.value.toISOString().split('T')[0]
    
    if (!availablePickupDates.value.has(pickupDateString)) {
      return true
    }
    
    if (!availableDropoffDates.value.has(dropoffDateString)) {
      return true
    }
    
    if (dropoffDate.value < pickupDate.value) {
      return true
    }
    
    return false
  })

  // Calculate duration between selected dates
  const calculateDuration = computed(() => {
    if (!pickupDate.value || !dropoffDate.value) return 0
    
    const startDate = new Date(pickupDate.value)
    const endDate = new Date(dropoffDate.value)
    const diffTime = endDate.getTime() - startDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays
  })

  const formatDate = (date: Date) => {
    if (!date) return ''
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
  }

  return {
    pickupDate,
    dropoffDate,
    availablePickupDates,
    availableDropoffDates,
    minDropoffDate,
    maxDropoffDate,
    disabledPickupDates,
    disabledDropoffDates,
    hasDateRangeConflict,
    calculateDuration,
    formatDate
  }
} 